import { StopTimeType } from "types/MultiStop";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { getClassicScheduleStopIndexes } from "@RequestFormV2/utils/buildClassicScheduleFromStops";

type StopFormField = keyof ScheduleStopValue;

// Address details are not inputs on the stop card; the address select shows errors set on address_id.
const ADDRESS_SELECT_FIELD: StopFormField = "address_id";

/**
 * Reverse of `mapStopToPickupFields` / `mapStopToDeliveryFields` in buildClassicScheduleFromStops.
 * Each classic key points at the stop field that renders its error.
 */
const PICKUP_KEY_TO_STOP_FIELD: Record<string, StopFormField> = {
  pickup_address_type: "stop_address_type",
  pickup_ready_date: "stop_after_date",
  pickup_ready_time: "stop_after_time",
  pickup_before_date: "stop_before_date",
  pickup_before_time: "stop_before_time",
  pickup_timezone: "stop_timezone",
  pickup_cargo_ready: "stop_time_type",
  pickup_contact_name: "stop_contact_name",
  pickup_contact_phone: "stop_contact_number",
  pickup_contact_email: "stop_contact_email",
  pickup_notes: "stop_notes",
  pickup_hours_from: "stop_hours_start",
  pickup_hours_to: "stop_hours_end",
  pickup_airport_code: "stop_airport_code",
  pickup_airport: "stop_airport_code",
  pickup_seaport_code: "stop_seaport_code",
  pickup_seaport: "stop_seaport_code",
  pickup_street: ADDRESS_SELECT_FIELD,
  pickup_street_line2: ADDRESS_SELECT_FIELD,
  pickup_address_name: ADDRESS_SELECT_FIELD,
  pickup_company_name: ADDRESS_SELECT_FIELD,
  pickup_city: ADDRESS_SELECT_FIELD,
  pickup_state: ADDRESS_SELECT_FIELD,
  pickup_country: ADDRESS_SELECT_FIELD,
  pickup_zipcode: ADDRESS_SELECT_FIELD,
  pickup_address_pk: ADDRESS_SELECT_FIELD,
  pickup_address_reference_number: ADDRESS_SELECT_FIELD,
  pickup_location_reference_number: ADDRESS_SELECT_FIELD,
  pickup_custom_address_reference: ADDRESS_SELECT_FIELD,
};

const DELIVERY_KEY_TO_STOP_FIELD: Record<string, StopFormField> = {
  stop1_address_type: "stop_address_type",
  stop1_after_date: "stop_after_date",
  stop1_after_time: "stop_after_time",
  stop1_before_date: "stop_before_date",
  stop1_before_time: "stop_before_time",
  stop1_timezone: "stop_timezone",
  delivery_cargo_ready: "stop_time_type",
  stop1_contact_name: "stop_contact_name",
  stop1_contact_phone: "stop_contact_number",
  stop1_contact_email: "stop_contact_email",
  stop1_notes: "stop_notes",
  delivery_hours_from: "stop_hours_start",
  delivery_hours_to: "stop_hours_end",
  stop1_airport_code: "stop_airport_code",
  stop1_airport: "stop_airport_code",
  stop1_seaport_code: "stop_seaport_code",
  stop1_seaport: "stop_seaport_code",
  stop1_street: ADDRESS_SELECT_FIELD,
  stop1_street_line2: ADDRESS_SELECT_FIELD,
  stop1_address_name: ADDRESS_SELECT_FIELD,
  stop1_company_name: ADDRESS_SELECT_FIELD,
  stop1_city: ADDRESS_SELECT_FIELD,
  stop1_state: ADDRESS_SELECT_FIELD,
  stop1_country: ADDRESS_SELECT_FIELD,
  stop1_zipcode: ADDRESS_SELECT_FIELD,
  destination_address_pk: ADDRESS_SELECT_FIELD,
  destination_address_reference_number: ADDRESS_SELECT_FIELD,
  stop1_location_reference_number: ADDRESS_SELECT_FIELD,
  stop1_custom_address_reference: ADDRESS_SELECT_FIELD,
};

const AFTER_TO_BEFORE_FIELD: Partial<Record<StopFormField, StopFormField>> = {
  stop_after_date: "stop_before_date",
  stop_after_time: "stop_before_time",
};

const BEFORE_TO_AFTER_FIELD: Partial<Record<StopFormField, StopFormField>> = {
  stop_before_date: "stop_after_date",
  stop_before_time: "stop_after_time",
};

/**
 * Stops keep defaults in the hidden After / Before inputs (e.g. delivery stop_after_time "17:00:00"),
 * and those are still sent, so point a date/time error at the input the stop's time type shows.
 */
function resolveVisibleTimeField(stop: ScheduleStopValue | undefined, field: StopFormField): StopFormField {
  if (stop?.stop_time_type === StopTimeType.BEFORE) return AFTER_TO_BEFORE_FIELD[field] ?? field;
  if (stop?.stop_time_type === StopTimeType.AFTER) return BEFORE_TO_AFTER_FIELD[field] ?? field;
  return field;
}

const firstMessage = (value: unknown): string | undefined => {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return undefined;
};

/** Classic schedule errors as [key, message]: DRF `{ key: ["msg"] }` or legacy `[{ title, message }]`. */
function readClassicScheduleErrors(scheduleErrors: unknown): [string, string][] {
  if (Array.isArray(scheduleErrors)) {
    return scheduleErrors.flatMap((item) => {
      const { title, message } = (item ?? {}) as { title?: unknown; message?: unknown };
      const text = firstMessage(message);
      return typeof title === "string" && text ? [[title, text] as [string, string]] : [];
    });
  }
  if (scheduleErrors && typeof scheduleErrors === "object") {
    return Object.entries(scheduleErrors).flatMap(([key, value]) => {
      const text = firstMessage(value);
      return text ? [[key, text] as [string, string]] : [];
    });
  }
  return [];
}

/**
 * Maps Quick Request classic schedule errors (pickup_* / stop1_*) onto `schedule.stops.{index}.{field}`.
 * Keys that are not classic stop fields are left for the toast.
 */
export function mapClassicScheduleErrorsToStops(
  scheduleErrors: unknown,
  stops: ScheduleStopValue[] | undefined,
): { path: string; message: string }[] {
  if (!Array.isArray(stops) || stops.length === 0) return [];

  const { pickup, delivery } = getClassicScheduleStopIndexes(stops);

  return readClassicScheduleErrors(scheduleErrors).flatMap(([key, message]) => {
    const pickupField = PICKUP_KEY_TO_STOP_FIELD[key];
    if (pickupField && pickup !== undefined) {
      const field = resolveVisibleTimeField(stops[pickup], pickupField);
      return [{ path: `schedule.stops.${pickup}.${field}`, message }];
    }

    const deliveryField = DELIVERY_KEY_TO_STOP_FIELD[key];
    if (deliveryField && delivery !== undefined) {
      const field = resolveVisibleTimeField(stops[delivery], deliveryField);
      return [{ path: `schedule.stops.${delivery}.${field}`, message }];
    }

    return [];
  });
}
